import { fmtINR } from "../../../lib/constants";
import { CheckCircle } from "@phosphor-icons/react";

// Module 7.2E4-Y — camera-only custody evidence types (GPS + server-stamped image).
export const EVIDENCE_TYPES = new Set([
  "PICKUP_PHOTO", "PICKUP_VEHICLE_LOADED", "SAL_SOURCE_RECEIPT", "AWB",
  "DESTINATION_RECEIPT_PHOTO", "VEHICLE_LOADED",
]);

export function EvidenceMeta({ doc }) {
  if (!EVIDENCE_TYPES.has(doc.doc_type)) return null;
  if (!doc.is_stamped_evidence) {
    return (
      <div className="text-[10px] text-amber-700" data-testid={`evidence-legacy-${doc.public_document_id}`}>
        Legacy evidence — no GPS stamp available
      </div>
    );
  }
  return (
    <div className="text-[10px] text-emerald-700" data-testid={`evidence-meta-${doc.public_document_id}`}>
      {doc.evidence_captured_at_ist}
      {doc.evidence_latitude != null && (
        <> · {Number(doc.evidence_latitude).toFixed(4)}, {Number(doc.evidence_longitude).toFixed(4)} · ±{Math.round(doc.evidence_accuracy_m)}m</>
      )}
      {doc.evidence_actor_role && <> · {doc.evidence_actor_role}</>}
    </div>
  );
}

export function Info({ label, children }) {
  return (
    <div>
      <div className="overline">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Module 7.2E4-Q — read-only accepted AUTO_ENGINE quote breakdown (commercial clarity).
// Shows the snapshot stored at acceptance (NOT recalculated): Air12 service charge, Load Assist
// line items, GST and the firm total. Insurance is always shown as separate/excluded. No edit
// controls for any role. Legacy MANUAL_SUPER_ADMIN quotes (no payable snapshot) render nothing.

export function QRow({ k, v, testid, strong }) {
  return (
    <div className="flex items-center justify-between py-1.5" data-testid={testid}>
      <dt className={strong ? "font-semibold" : "text-[hsl(var(--muted))]"}>{k}</dt>
      <dd className={`text-right mono ${strong ? "font-semibold" : ""}`}>{v}</dd>
    </div>
  );
}

export function AcceptedQuoteBreakdown({ quote, showPricingVersion }) {
  const total = quote?.payable != null ? quote.payable : quote?.total_payable;
  if (!quote || total == null) return null;
  const la = quote.load_assist;
  const hasLA = la && la.available;
  const cargo = quote.air12_service_charge != null
    ? quote.air12_service_charge
    : quote.subtotal_excl_insurance != null
      ? quote.subtotal_excl_insurance
      : ["core_custody_fee", "weight_charge", "source_local_movement_charge", "destination_local_movement_charge"]
          .reduce((s, k) => s + (Number(quote[k]) || 0), 0);
  const gst = quote.gst_amount != null ? quote.gst_amount : quote.gst;
  return (
    <div className="grid-card p-3 text-sm" data-testid="accepted-quote-breakdown">
      <div className="overline mb-2">AIR12 PAYMENT BREAKDOWN</div>
      <dl className="divide-y divide-[hsl(var(--border))]">
        <QRow k="Air12 service charge" v={fmtINR(cargo)} testid="aqb-service-charge" />
        {hasLA && la.pickup?.selected && (
          <QRow k={la.pickup.label} v={fmtINR(la.pickup.amount)} testid="aqb-load-assist-pickup" />
        )}
        {hasLA && la.delivery?.selected && (
          <QRow k={la.delivery.label} v={fmtINR(la.delivery.amount)} testid="aqb-load-assist-delivery" />
        )}
        <QRow k="Cargo insurance (included)" v={fmtINR(quote.insurance_premium || 0)} testid="aqb-insurance" />
        {gst != null && (
          <QRow k={`GST${quote.gst_rate != null ? ` (${quote.gst_rate}%)` : ""}`} v={fmtINR(gst)} testid="aqb-gst" />
        )}
        {Number(quote.round_off) !== 0 && quote.round_off != null && (
          <QRow k="Round Off" v={`${Number(quote.round_off) < 0 ? "− " : "+ "}${fmtINR(Math.abs(Number(quote.round_off)))}`} testid="aqb-round-off" />
        )}
        <QRow k="Total Air12 payable" v={fmtINR(total)} testid="aqb-total" strong />
      </dl>
      <p className="text-[11px] text-[hsl(var(--muted))] mt-2" data-testid="aqb-payable-note">
        One payment. Mandatory cargo insurance is included as a pre-GST line; GST is applied once to the total.
      </p>
      {showPricingVersion && (quote.pricing_version || quote.pricing_settings_version != null) && (
        <div className="text-[11px] text-[hsl(var(--muted))] mt-2" data-testid="aqb-pricing-version">
          Pricing {quote.pricing_version || "—"}{quote.pricing_settings_version != null ? ` · settings v${quote.pricing_settings_version}` : ""} · snapshot fixed at acceptance
        </div>
      )}
    </div>
  );
}

// P3 — plain, user-facing copy for the new lifecycle error codes. Never leaks enum values,
// Quote/Payment ids, storage paths or stack traces.
const ERROR_COPY = {
  DRAFT_STALE: "This draft was updated elsewhere. Reopen it to see the latest details before continuing.",
  DRAFT_LOCKED: "This booking is confirmed and can no longer be edited.",
  CHECKOUT_SUPERSEDED: "Your payment details changed. Please start a fresh checkout with the latest amount.",
  STALE_CHECKOUT: "This checkout is no longer current. Please refresh and start a fresh checkout.",
  CHECKOUT_NOT_PENDING: "This checkout is no longer active. Please start a fresh checkout.",
  QUOTE_CHANGED: "The quote has changed. Please review the latest amount and start a fresh checkout.",
  QUOTE_NOT_AVAILABLE: "No active quote is available yet for this shipment.",
  AMOUNT_MISMATCH: "The amount does not match the current payable. Please refresh and try again.",
  PAYMENT_ALREADY_COMPLETED: "This payment has already been completed.",
  PAYMENT_IN_PROGRESS: "A payment is already being processed for this booking.",
  OPERATIONS_LOCKED: "Physical operations are locked until payment, insurance, the final declaration and documents are complete.",
  WET_SIGNATURE_REQUIRED: "A wet-signature confirmation is required to complete this step.",
  INSURANCE_NOT_CONFIRMED: "This step is available after Air12 confirms cargo insurance.",
  PAYMENT_NOT_COMPLETED: "This step is available after the Air12 payment is completed.",
  INSURANCE_LOCKED: "Insurance is confirmed and can no longer be changed.",
  ROUTE_NOT_ACTIVE: "This route is not currently active.",
  ROUTE_RETIRED: "This action is no longer available.",
  MOVEMENT_STARTED: "Movement has already started; this action is no longer available.",
  ALREADY_CANCELLED: "This shipment is already cancelled.",
  CANCELLATION_PREVIEW_STALE: "The cancellation calculation has changed. Please review the updated amount before confirming.",
  CANCELLATION_UNAVAILABLE: "This order can no longer be cancelled.",
  CANCELLATION_STATE_INCONSISTENT: "This order can no longer be cancelled.",
  ADMIN_REASON_REQUIRED: "A reason for cancelling on behalf of the Sender is required.",
  REFUND_REFERENCE_REQUIRED: "A refund reference is required.",
  REFUND_ALREADY_COMPLETED: "This refund is already completed and cannot be changed.",
  REFUND_NOT_PENDING: "There is no pending refund to complete.",
};

export function friendlyError(e, fallback = "Something went wrong. Please try again.") {
  const detail = e?.response?.data?.detail;
  const code = detail && typeof detail === "object" ? detail.code : undefined;
  if (code === "ROUTE_DAY_FULL") {
    return detail.message || "This route is fully booked for today. Please try again tomorrow or request a callback.";
  }
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  if (typeof detail === "string") return detail;
  return e?.displayMessage || fallback;
}

// 7.2E3 — Commercial gate: manual insurance + Air12 service payment (sender submit / admin verify).

export function GateChip({ label, value, sub, tone, testid }) {
  const toneCls = {
    ok: "text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.4)]",
    bad: "text-[hsl(var(--danger))] border-[hsl(var(--danger)/0.4)]",
    warn: "text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.4)]",
    info: "text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.3)]",
    muted: "text-[hsl(var(--muted))] border-[hsl(var(--border))]",
  }[tone] || "text-[hsl(var(--muted))] border-[hsl(var(--border))]";
  return (
    <div className={`grid-card p-2 border ${toneCls}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-semibold text-sm mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-[hsl(var(--muted))]">{sub}</div>}
    </div>
  );
}

export function GateStep({ n, label, done, current, last, children }) {
  return (
    <div className="flex gap-3">
      <div className={`grid place-items-center w-6 h-6 rounded-full text-xs font-bold shrink-0 ${done ? "bg-[hsl(var(--accent))] text-white" : current ? "border-2 border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border border-[hsl(var(--border))] text-[hsl(var(--muted))]"}`}>
        {done ? <CheckCircle size={14} weight="bold" /> : n}
      </div>
      <div className={`flex-1 ${last ? "" : "pb-1"}`}>
        <div className={`text-sm font-semibold ${current ? "text-[hsl(var(--fg))]" : "text-[hsl(var(--muted))]"}`}>{label}</div>
        {children}
      </div>
    </div>
  );
}

export function Panel({ icon, title, children, action }) {
  return (
    <section className="grid-card">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">{icon}<h3 className="heading text-sm">{title}</h3></div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

// M2 — City-Based Operations: source/destination Controllers (CTRL) + two-CTOP model.

export function Fig({ label, value, testid, danger }) {
  return (
    <div className="grid-card p-2" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`mono text-sm font-semibold mt-0.5 ${danger ? "text-[hsl(var(--danger))]" : ""}`}>{value}</div>
    </div>
  );
}

export function friendlyEvent(e) {
  if (e.event_type === "LOAD_ASSIST_CONFIRMED") {
    const side = e.meta?.side;
    return side === "pickup" ? "Load Assist arrangement ready — Pickup Side"
      : side === "delivery" ? "Load Assist arrangement ready — Delivery Side"
        : "Load Assist arrangement ready";
  }
  if (e.event_type === "NOTIFICATION_RESENT") {
    return `Notification re-sent${e.meta?.trigger ? ` · ${e.meta.trigger}` : ""}`;
  }
  return null;
}

// Module 7.2E4-R / Email Health — notification log + explicit resend (pilot support).
// SUPER_ADMIN: full view + resend (non-OTP). CTRL/CTOP: read-only for shipments they can view.
